'use client';

import React, { useState } from 'react';

import { Rating } from '@bytel/product-wall';
import {
    Align,
    Justify,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TitleMarkup,
    View,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import { WallType } from '@app-types/config';
import { ProductChildFull } from '@app-types/product';
import { ReviewsInfoType } from '@app-types/review';

import { PriceGuaranteeModal } from '@components/price-guarantee-modal';
import { PromoBorderedBlock } from '@components/promo-bordered-block';

import { TabsEnum } from '@product-detail/components';

const config = getAppConfig();
const certifieCircleImage = `${config.assets.cmsImages.url}/original/image/CMS/ECOMMERCE/Logo_certifie__circle.svg`;

export type ProductHeaderProps = {
    type: WallType;
    isMobile?: boolean;
    currentProduct: ProductChildFull;
    reviews?: ReviewsInfoType;
    setActiveTab: (tab: TabsEnum) => void;
    isSmartChange: boolean;
    handleOpenSmartChangeModal?: () => void;
};

export function ProductHeader({ type, currentProduct, reviews, setActiveTab }: Readonly<ProductHeaderProps>) {
    const { parentTitle, color, capacity } = currentProduct;
    const [isPriceGuaranteeModalOpen, setIsPriceGuaranteeModalOpen] = useState(false);

    const handleOpenPriceGuaranteeModal = () => {
        setIsPriceGuaranteeModalOpen(true);
    };

    const scrollToTabs = (tab: TabsEnum, id: string) => {
        setActiveTab(tab);

        // setTimeOut in order to delay the execution of scrollIntoView
        // until after the state update has been applied and the component has re-rendered
        setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({
                behavior: 'smooth',
            });
            const tabId = document.getElementById(`${tab}-tab`) as HTMLDivElement | null;

            tabId?.focus();
        }, 0);
    };

    return (
        <div className='product-title'>
            <View flexable justify={Justify.SPACE_BETWEEN} className='gap-medium is-flex-col-touch'>
                <div className='has-text-centered-touch'>
                    <Title markup={TitleMarkup.H1} level={TitleLevels.FOUR} marginless>
                        {parentTitle}
                    </Title>
                    <Text markup={TextMarkup.SPAN} level={TextLevels.TWO} marginless>
                        {`${color ?? ''} ${capacity ?? ''}`}
                    </Text>
                </div>
                {reviews && (
                    <View
                        flexable
                        align={Align.CENTER}
                        className='gap-medium w-auto desktop-justify-end cursor-pointer is-justified-center-touch'
                        onClick={() => scrollToTabs(TabsEnum.REVIEWS, 'reviews-section')}
                    >
                        <Rating rating={reviews.note} />
                        <button className='button-clear link'>{reviews.reviewCount}&nbsp;avis</button>
                    </View>
                )}
            </View>
            {type === WallType.PHONE && (
                <>
                    <Spacer size={SpacerSize.FOUR} />
                    <PromoBorderedBlock
                        badge='Nouveau'
                        imageSrc={certifieCircleImage}
                        onLinkClick={handleOpenPriceGuaranteeModal}
                    />
                </>
            )}
            <PriceGuaranteeModal
                isOpen={isPriceGuaranteeModalOpen}
                handleClose={() => setIsPriceGuaranteeModalOpen(false)}
            />
        </div>
    );
}
